import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RxCross2 } from "react-icons/rx";
import { HiOutlineUserCircle } from "react-icons/hi2";

const ROLE_LABELS = {
  1: "Super Admin",
  2: "Admin",
  3: "Alliance Agent",
  4: "Campaign Manager",
  5: "Audit Manager",
};

const ShowUserModal = ({ isOpen, onClose, user }) => {
  const roleLabel = ROLE_LABELS[user?.user_roleid] || `Role ${user?.user_roleid ?? "-"}`;
  const statusLabel = user?.status ? "Active" : "Inactive";

  return (
    <AnimatePresence>
      {isOpen && user && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 backdrop-blur-sm px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            initial={{ opacity: 0, y: 26, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 14, scale: 0.98 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className="w-full max-w-lg rounded-2xl overflow-hidden border border-transparent shadow-2xl relative backdrop-blur-md"
            style={{
              background:
                "linear-gradient(155deg, rgba(8, 17, 36, 0.95) 0%, rgba(18, 32, 62, 0.88) 100%) padding-box, linear-gradient(125deg, rgba(56, 189, 248, 0.85) 0%, rgba(59, 130, 246, 0.85) 45%, rgba(99, 102, 241, 0.85) 100%) border-box",
            }}
          >
            <div className="h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-500" />
            <button
              onClick={onClose}
              className="absolute top-4 right-4 h-8 w-8 rounded-full text-white grid place-items-center shadow-sm transition hover:brightness-105"
              style={{ backgroundImage: "linear-gradient(90deg, #6366f1 0%, #2563eb 100%)" }}
              aria-label="Close details"
            >
              <RxCross2 size={20} />
            </button>

            <div className="px-6 pt-6 pb-4 text-white">
              <div className="flex items-center gap-4">
                <div className="h-14 w-14 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white grid place-items-center shadow">
                  <HiOutlineUserCircle size={28} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xl font-extrabold text-white truncate">{user.user_name}</h3>
                  <p className="text-sm text-white bg-gradient-to-r from-cyan-500 to-blue-600 border border-white/40 rounded-md px-2 py-0.5 inline-block truncate">
                    {user.user_email}
                  </p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="px-3 py-1 rounded-full border border-cyan-300/60 bg-cyan-600/35 text-white text-xs font-semibold uppercase tracking-wide">
                  {roleLabel}
                </span>
                <span
                  className={`px-3 py-1 rounded-full border text-xs font-semibold uppercase tracking-wide ${
                    user.status
                      ? "border-emerald-300/60 bg-emerald-600/35 text-white"
                      : "border-rose-300/60 bg-rose-600/35 text-white"
                  }`}
                >
                  {statusLabel}
                </span>
              </div>
            </div>

            <div className="px-6 pb-6">
              <div className="rounded-xl border border-white/35 bg-slate-900/45 overflow-hidden">
                <Detail label="User ID" value={user.user_id} />
                <Detail label="Mobile Number" value={user.mobile_no || "-"} />
                <Detail label="Role ID" value={user.user_roleid} isLast={false} />
                <Detail label="Account Status" value={statusLabel} isLast />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Detail = ({ label, value, isLast = false }) => (
  <div className={`flex justify-between items-center px-4 py-3 ${isLast ? "" : "border-b border-white/20"}`}>
    <span className="text-sm font-semibold text-white">{label}</span>
    <span
      className={`text-sm font-semibold px-2.5 py-1 rounded-md border ${
        label.includes("Status")
          ? value === "Active"
            ? "text-white bg-emerald-600/35 border-emerald-300/60"
            : "text-white bg-rose-600/35 border-rose-300/60"
          : label.includes("Role")
            ? "text-white bg-violet-600/35 border-violet-300/60"
            : "text-white bg-cyan-600/35 border-cyan-300/60"
      }`}
    >
      {value}
    </span>
  </div>
);

export default ShowUserModal;
